<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.vehicle_category, 'Categorie')"
      :items="vehicleCategories"
      item-title="name"
      item-value="id"
      :loading="loading"
      :disabled="loading"
      name="category"
      v-model="selectedCategory"
      :rules="[v => !!v || translation(translationList.required, 'Acest camp este obligatoriu')]"
      return-object
  >
    <template v-slot:append-inner>
      <v-tooltip
          location="top"
          open-delay="500"
          open-on-click open-on-hover
      >
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" class="cursor-pointer" aria-label="info">
            mdi-information
          </v-icon>
        </template>
        <span><NuxtImg
            src="/images/vehicle/registration_template/template_j.png"
            class="h-100"
            loading="lazy"
            alt="Registration template"
        /></span>
      </v-tooltip>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, toRefs, watch } from 'vue';
import { useVehicleStore } from '~/plugins/store/vehicle';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {VehicleCategory} from "~/types/Vehicle";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => VehicleCategory | null,
      default: () => null,
      required: false
    },
  },
  emits: ['update:modelValue', 'validation-vehicle-status'],
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const vehicleStore = useVehicleStore();
    const translationStore = useTranslationStore();
    const selectedCategory = ref(modelValue.value ? modelValue.value : { id: '', name: '', ws_id: null });
    const vehicleCategories = ref([]);
    const translationList = ref<TranslationList>({});
    const loading = ref(false);
    const error = ref([]);

    onMounted(async () => {
      loading.value = true;
      const categories = await vehicleStore.getVehicleCategories();
      vehicleCategories.value = categories.map(category => ({
        id: category.id,
        name: category.name,
        ws_id: parseInt(category.ws_id),
        code: category.code,
      }));
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      if (!modelValue.value && vehicleCategories.value.length > 0) {
        selectedCategory.value = vehicleCategories.value[0];
        emit('update:modelValue', selectedCategory.value);
      }
      loading.value = false;
      const isValid = !!selectedCategory.value;
      emit('validation-vehicle-status', { field: 'rca_mapped_category', isValid });

      // Listen for validate event
      window.addEventListener('validate-vehicle', (data) => {
        if (data.detail && data.detail === 'rca_mapped_category') {
          if (!selectedCategory.value) {
            error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
          } else {
            error.value = [];
            emit('validation-vehicle-status', { field: 'rca_mapped_category', isValid: true });
          }
        }
      });
    });

    watch(selectedCategory, (newValue) => {
      emit('update:modelValue', newValue);
    });

    watch(modelValue, (newValue) => {
      if (newValue && (newValue.id !== selectedCategory.value?.id)) {
        selectedCategory.value = newValue;
        error.value = [];
      }
      const isValid = !!newValue;
      emit('validation-vehicle-status', { field: 'rca_mapped_category', isValid });
    });

    return {
      selectedCategory,
      translationList,
      translation,
      vehicleCategories,
      loading,
      error,
    };
  },
});
</script>

<style scoped>
</style>
